import axios from 'axios';
import toast from 'react-hot-toast';

// Configuration de base d'Axios
const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Fonction utilitaire pour retry automatique (sans récursion)
const retryRequest = async (fn, retries = 3, delay = 1000) => {
  let lastError;

  for (let i = 0; i <= retries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (i < retries && (error.code === 'NETWORK_ERROR' || error.response?.status >= 500)) {
        await new Promise((resolve) => setTimeout(resolve, delay * 2 ** i));
      } else {
        throw error;
      }
    }
  }

  throw lastError;
};

// Intercepteur pour gérer les erreurs et la déconnexion automatique
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // 401 Jira (requiresJiraAuth) : pas de déconnexion, juste propager
      if (error.response.data?.requiresJiraAuth === true) {
        return Promise.reject(error);
      }

      // Token expiré ou invalide
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('user_data');

      if (error.response.data?.code === 'TOKEN_EXPIRED') {
        toast.error('Session expirée, veuillez vous reconnecter');
      } else {
        toast.error('Non autorisé, veuillez vous connecter');
      }

      // Ne pas rediriger ici, laisser React Router gérer la navigation
      // La redirection sera gérée par useAuth ou les composants
    } else if (error.response?.status >= 500) {
      // 503 Jira (non configuré) : silencieux dans l'UI
      if (error.request?.responseURL?.includes('/jira/ticket/')) {
        return Promise.reject(error);
      }
      toast.error('Erreur serveur, veuillez réessayer plus tard');
    } else if (error.code === 'NETWORK_ERROR') {
      toast.error('Erreur de connexion au serveur');
    }

    return Promise.reject(error);
  }
);

// Services d'authentification
export const authService = {
  /**
   * @param {string} identifier - Username or email
   * @param {string} password
   * @returns {Promise<import('@shared/shared').LoginResponse>}
   */
  login: async (identifier, password) => {
    const isEmail = identifier.includes('@');
    const body = isEmail ? { email: identifier, password } : { username: identifier, password };
    const response = await retryRequest(() => api.post('/auth/login', body));

    if (response.data.success) {
      sessionStorage.setItem('auth_token', response.data.token);
      sessionStorage.setItem('user_data', JSON.stringify(response.data.user));
    }

    return response.data;
  },

  register: async (username, email, password, firstName, lastName) => {
    const response = await api.post('/auth/register', {
      username,
      email,
      password,
      firstName,
      lastName
    });
    return response.data;
  },

  /**
   * @returns {Promise<{ success: boolean; user: import('@shared/shared').User }>}
   */
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  /**
   * @param {string} currentPassword
   * @param {string} newPassword
   * @returns {Promise<{ success: boolean; message?: string }>}
   */
  changeMyPassword: async (currentPassword, newPassword) => {
    const response = await api.post('/auth/me/password', { currentPassword, newPassword });
    return response.data;
  },

  /**
   * Met à jour le prénom et le nom de l'utilisateur courant.
   * @param {{ firstName: string; lastName: string }} data
   * @returns {Promise<import('@shared/shared').User>}
   */
  updateMyProfile: async ({ firstName, lastName }) => {
    const response = await api.put('/auth/me/profile', { firstName, lastName });
    return response.data;
  },

  /**
   * Sauvegarde la préférence thème de l'utilisateur courant en DB.
   * @param {'light' | 'dark' | 'system'} theme
   */
  updateMyTheme: async (theme) => {
    const response = await api.put('/auth/me/theme', { theme });
    return response.data;
  },

  logout: () => {
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('user_data');
    // Ne pas rediriger ici, laisser useAuth gérer la navigation
  },

  getCurrentUser: () => {
    const userData = sessionStorage.getItem('user_data');
    return userData ? JSON.parse(userData) : null;
  },

  isAuthenticated: () => {
    return !!sessionStorage.getItem('auth_token');
  },

  verifyToken: async () => {
    try {
      const response = await api.post('/auth/verify-token');
      return response.data.valid;
    } catch (_error) {
      return false;
    }
  },

  startSsoLogin: () => axios.get('/api/auth/sso/start').then((r) => r.data),

  // ── Personal Access Tokens (PAT) ─────────────────────────────────────────
  listTokens: async () => {
    const response = await api.get('/auth/tokens');
    return response.data;
  },

  createToken: async (name) => {
    const response = await api.post('/auth/tokens', { name });
    return response.data;
  },

  revokeToken: async (tokenId) => {
    const response = await api.delete(`/auth/tokens/${tokenId}`);
    return response.data;
  }
};

// Services d'administration des utilisateurs
export const adminUsersService = {
  /**
   * @returns {Promise<{ success: boolean; users: import('@shared/shared').User[] }>}
   */
  listUsers: async () => {
    const response = await api.get('/auth/admin/users');
    return response.data;
  },

  resetUserPassword: async (userId, newPassword) => {
    const response = await api.post(`/auth/admin/users/${userId}/reset-password`, { newPassword });
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await api.delete(`/auth/admin/users/${userId}`);
    return response.data;
  }
};

// Services de monitoring des serveurs
export const serversService = {
  getStatus: async () => {
    return retryRequest(async () => {
      const response = await api.get('/servers/status'); // Retour à l'endpoint normal
      return response.data;
    });
  },

  // Force une mesure immédiate au lieu de relire le dernier état connu. Le
  // backend sonde en continu pour son propre compte ; ceci sert au bouton
  // « Actualiser », pour lequel relire un cache serait un clic sans effet.
  refreshStatus: async () => {
    const response = await api.post('/servers/refresh');
    return response.data;
  },

  getInstanceStatus: async (ip, port) => {
    return retryRequest(async () => {
      const response = await api.get(`/servers/${ip}/${port}/status`);
      return response.data;
    });
  },

  getMetrics: async (ip, port, period = '1h') => {
    return retryRequest(async () => {
      const response = await api.get(`/servers/metrics/${ip}/${port}?period=${period}`);
      return response.data;
    });
  },

  testConnection: async (serverIp) => {
    return retryRequest(async () => {
      const response = await api.post('/servers/test-connection', { serverIp });
      return response.data;
    });
  },

  assignPort: (ip, port, jiraTicket) =>
    api
      .post(`/servers/${encodeURIComponent(ip)}/${encodeURIComponent(port)}/assignments`, {
        jiraTicket
      })
      .then((r) => r.data),

  removeAssignment: (assignmentId) =>
    api.delete(`/servers/assignments/${assignmentId}`).then((r) => r.data),

  setRecetteDate: (assignmentId) =>
    api.put(`/servers/assignments/${assignmentId}/recette`).then((r) => r.data),

  clearRecetteDate: (assignmentId) =>
    api.delete(`/servers/assignments/${assignmentId}/recette`).then((r) => r.data)
};

// Services de déploiement
export const deployService = {
  deploy: async (ip, port, file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);

    if (options.newdb) {
      formData.append('newdb', 'true');
    }

    const response = await api.post(`/deploy/${ip}/${port}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 300000, // 5 minutes pour les déploiements
      onUploadProgress: options.onProgress
    });

    return response.data;
  },

  getHistory: async (params = {}) => {
    const response = await api.get('/deploy/history', { params });
    return response.data;
  },

  getDeploymentDetails: async (deploymentId) => {
    const response = await api.get(`/deploy/${deploymentId}`);
    return response.data;
  },

  getStats: async (period = '7d') => {
    const response = await api.get(`/deploy/stats/summary?period=${period}`);
    return response.data;
  }
};

// Services de logs
export const logsService = {
  getLogs: async (ip, port, params = {}) => {
    // Le password SSH passe par le body (POST) pour éviter d'apparaître dans
    // les logs HTTP côté backend/proxy. `lines` reste en query (non sensible).
    const queryString = params.lines ? `?lines=${encodeURIComponent(params.lines)}` : '';
    const url = `/logs/${ip}/${port}${queryString}`;
    const body = params.password ? { password: params.password } : {};

    return retryRequest(async () => {
      const response = await api.post(url, body);
      return response.data;
    });
  },

  // WebSocket pour les logs en temps réel
  createLogsWebSocket: (onMessage, onError = console.error) => {
    const token = sessionStorage.getItem('auth_token');

    if (!token) {
      onError(new Error('No authentication token available'));
      return null;
    }

    // Construire l'URL WebSocket via le même host/port que la page
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/ws?token=${encodeURIComponent(token)}`;

    let ws = null;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
    let reconnectTimer = null;

    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          console.log('WebSocket connection opened for logs');
          reconnectAttempts = 0;
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            onMessage(data);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          onError(error);
        };

        ws.onclose = (event) => {
          console.log('WebSocket connection closed:', event.code, event.reason);

          // Reconnexion automatique si ce n'est pas une fermeture intentionnelle
          if (event.code !== 1000 && reconnectAttempts < maxReconnectAttempts) {
            reconnectAttempts++;
            const delay = Math.min(1000 * 2 ** reconnectAttempts, 30000);
            console.log(
              `Attempting WebSocket reconnection ${reconnectAttempts}/${maxReconnectAttempts} in ${delay}ms`
            );

            reconnectTimer = setTimeout(() => {
              if (reconnectAttempts <= maxReconnectAttempts) {
                connect();
              }
            }, delay);
          }
        };
      } catch (error) {
        console.error('Failed to create WebSocket connection:', error);
        onError(error);
      }
    };

    connect();

    return {
      subscribe: (ip, port) => {
        const attemptSubscribe = () => {
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(
              JSON.stringify({
                type: 'subscribe_logs',
                ip,
                port
              })
            );
            return true;
          }
          return false;
        };

        // Essayer immédiatement
        if (attemptSubscribe()) {
          return;
        }

        // Si pas prêt, attendre l'événement 'open'
        if (ws && ws.readyState === WebSocket.CONNECTING) {
          console.log('WebSocket connecting, waiting for open event...');

          const onOpen = () => {
            console.log('WebSocket opened, attempting subscription...');
            attemptSubscribe();
            ws.removeEventListener('open', onOpen);
          };

          ws.addEventListener('open', onOpen);
        } else {
          console.warn(
            'WebSocket not available or in bad state:',
            ws ? ws.readyState : 'no websocket'
          );
        }
      },

      unsubscribe: () => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(
            JSON.stringify({
              type: 'unsubscribe_logs'
            })
          );
        }
      },

      close: () => {
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
        reconnectAttempts = maxReconnectAttempts; // Empêcher les reconnexions
        if (ws) {
          ws.close(1000, 'Client closing connection');
        }
      },

      getReadyState: () => (ws ? ws.readyState : WebSocket.CLOSED)
    };
  }
};

// Services Jira OAuth
export const jiraService = {
  getAuthStatus: () => api.get('/jira/auth/status').then((r) => r.data),
  startAuth: () => api.get('/jira/auth/start').then((r) => r.data),
  disconnect: () => api.delete('/jira/auth').then((r) => r.data),
  getTicket: (key) => api.get(`/jira/ticket/${encodeURIComponent(key)}`).then((r) => r.data)
};

export default api;
